import type { LayoutProps, SpacingProps, TextProps, VariantProps } from '@shopify/restyle';
import { createRestyleComponent, createVariant, layout, spacing, useTheme } from '@shopify/restyle';
import { useAuthorize } from 'app/hooks/use-authorize';
import type { PropsWithChildren, ReactNode, RefObject } from 'react';
import React from 'react';
import type {
  GestureResponderEvent,
  PressableProps,
  StyleProp,
  ViewStyle,
  PressableStateCallbackType,
} from 'react-native';
import { ActivityIndicator, View, StyleSheet, Pressable } from 'react-native';
import { Pressable as GHPressable } from 'react-native-gesture-handler';

import { Row } from './box';
import { Text } from './text';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconInfoCircle } from '@tabler/icons-react-native';
import type { Theme } from 'app/shopify';
import type { AuthenticationGateSource } from 'app/atoms';
import { PressableType } from './button.constants';

export { PressableType } from './button.constants';

enum Variant {
  Primary = 'primary',
  Accent = 'accent',
  Secondary = 'secondary',
  SecondaryLight = 'secondaryLight',
  Delete = 'delete',
  Signup = 'signup',
  WhiteWithBorder = 'whiteWithBorder',
  Plain = 'plain',
}

type RestyleProps = LayoutProps<Theme> & SpacingProps<Theme> & VariantProps<Theme, 'buttonVariants'>;

export interface ButtonProps
  extends
    RestyleProps,
    Pick<
      PressableProps,
      | 'accessibilityHint'
      | 'accessibilityLabel'
      | 'accessibilityRole'
      | 'disabled'
      | 'hitSlop'
      | 'onLongPress'
      | 'onPress'
      | 'testID'
    > {
  pressableType?: PressableType;
  authorize?: boolean;
  authorizationSource?: AuthenticationGateSource;
  children: ReactNode;
  loading?: boolean;
  prefix?: ReactNode;
  style?: StyleProp<ViewStyle>;
  suffix?: ReactNode;
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify';
  innerRef?: RefObject<View | null>;
  pureChildren?: boolean;
  infoButtonOnPress?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}

const Base = createRestyleComponent<RestyleProps & PressableProps & { pressableType?: PressableType }, Theme>(
  [createVariant({ themeKey: 'buttonVariants' }), layout, spacing],
  ({ pressableType = PressableType.Default, ...props }) =>
    pressableType === PressableType.Default ? <Pressable {...props} /> : <GHPressable {...props} />,
);

function PureWrapper({ children }: PropsWithChildren) {
  return children;
}

function getButtonTextProps(functionallyDisabled: boolean | undefined, variant: ButtonProps['variant']) {
  if (
    functionallyDisabled &&
    (variant === Variant.Accent ||
      variant === Variant.Secondary ||
      variant === Variant.SecondaryLight ||
      variant === Variant.Delete)
  ) {
    return { color: 'text.placeholder' } as const;
  }
  if (functionallyDisabled) return { color: 'white' } as const;
  if (variant === Variant.Accent || variant === Variant.Secondary || variant === Variant.SecondaryLight) {
    return { color: 'black' } as const;
  }
  if (variant === Variant.Signup) return { color: 'white', fontWeight: '700' } as const;
  if (variant === Variant.Primary) return { color: 'white' } as const;
  if (variant === Variant.Delete) return { color: 'text.error' } as const;
  return null;
}

function getButtonDisabledStyle({
  forcedBackgroundColor,
  functionallyDisabled,
  theme,
  variant,
}: {
  forcedBackgroundColor: ViewStyle['backgroundColor'];
  functionallyDisabled: boolean | undefined;
  theme: Theme;
  variant: ButtonProps['variant'];
}): StyleProp<ViewStyle> {
  if (!functionallyDisabled) return null;
  if (variant === Variant.Accent || variant === Variant.Primary || variant === Variant.Signup) {
    return {
      backgroundColor: forcedBackgroundColor,
      borderRadius: variant === Variant.Signup ? theme.borderRadii['radius.xxl'] : theme.borderRadii['radius.xl'],
    };
  }
  if (variant === Variant.Secondary || variant === Variant.SecondaryLight || variant === Variant.Delete) {
    return { borderColor: '#999999', borderRadius: theme.borderRadii['radius.xl'] };
  }
  return null;
}

function ButtonPrefix({ flexDirection, prefix }: { flexDirection: ButtonProps['flexDirection']; prefix?: ReactNode }) {
  if (!prefix) return null;
  return (
    <Row {...(flexDirection === 'row' ? { marginEnd: 'spacing.general.x2' } : { marginBottom: 'spacing.general.x2' })}>
      {prefix}
    </Row>
  );
}

function ButtonMainContent({
  centeredContent,
  children,
  flexDirection,
  prefix,
  pureChildren,
  suffix,
  textAlign,
  textProps,
}: {
  centeredContent: boolean;
  children: ReactNode;
  flexDirection: ButtonProps['flexDirection'];
  prefix?: ReactNode;
  pureChildren: boolean;
  suffix?: ReactNode;
  textAlign: ButtonProps['textAlign'];
  textProps: TextProps<Theme> | null;
}) {
  if (typeof children !== 'string') return pureChildren ? <PureWrapper>{children}</PureWrapper> : children;
  return (
    <Text
      {...textProps}
      {...(flexDirection === 'row' && {
        numberOfLines: 1,
        ...(centeredContent ? {} : { style: { flex: 1 } }),
      })}
      textAlign={textAlign ?? (!prefix && !suffix ? 'center' : undefined)}
    >
      {children}
    </Text>
  );
}

function ButtonSuffix({
  flexDirection,
  suffix,
  variant,
}: {
  flexDirection: ButtonProps['flexDirection'];
  suffix?: ReactNode;
  variant: ButtonProps['variant'];
}) {
  if (!suffix) return null;
  return (
    <Row
      {...(flexDirection === 'row'
        ? { marginStart: variant === 'signup' ? 'spacing.general.0' : 'spacing.general.x2' }
        : { marginTop: 'spacing.general.x2' })}
    >
      {suffix}
    </Row>
  );
}

function ButtonLoadingOverlay({
  disabledStyle,
  loading,
  theme,
  variant,
}: {
  disabledStyle: StyleProp<ViewStyle>;
  loading?: boolean;
  theme: Theme;
  variant: ButtonProps['variant'];
}) {
  if (!loading) return null;
  const onDefaultSurface =
    variant === Variant.Secondary ||
    variant === Variant.SecondaryLight ||
    variant === Variant.WhiteWithBorder ||
    variant === Variant.Delete;
  return (
    <Row
      backgroundColor={onDefaultSurface ? 'surface.default' : undefined}
      bottom={0}
      justifyContent="center"
      left={0}
      position="absolute"
      right={0}
      style={disabledStyle}
      top={0}
    >
      <ActivityIndicator color={onDefaultSurface ? theme.colors['text.primary'] : theme.colors['surface.default']} />
    </Row>
  );
}

function ButtonInfoEnd({
  infoButtonOnPress,
  pressableType,
  theme,
}: {
  infoButtonOnPress?: () => void;
  pressableType: PressableType;
  theme: Theme;
}) {
  if (!infoButtonOnPress) return null;
  const ConditionalPressable = pressableType === PressableType.Default ? Pressable : GHPressable;
  return (
    <View style={{ position: 'absolute', right: 0 }}>
      <ConditionalPressable onPress={infoButtonOnPress}>
        <Row>
          <View style={{ width: 1, backgroundColor: theme.colors['text.primary'], height: '100%' }} />
          <View style={{ marginTop: 2, paddingHorizontal: theme.spacing['spacing.general.x1'] }}>
            <IconInfoCircle color="transparent" size={36} strokeWidth={ICON_STROKE_WIDTH} />
          </View>
        </Row>
      </ConditionalPressable>
    </View>
  );
}

export function Button({
  pressableType = PressableType.Default,
  alignItems = 'center',
  authorize,
  authorizationSource = 'unknown',
  children,
  disabled,
  justifyContent = 'center',
  loading,
  onPress,
  onHoverIn,
  onHoverOut,
  prefix,
  style,
  suffix,
  textAlign,
  variant,
  flexDirection: flexDirectionProp,
  innerRef,
  pureChildren = true,
  infoButtonOnPress,
  ...rest
}: PropsWithChildren<ButtonProps>) {
  const flexDirection = flexDirectionProp === undefined && variant !== Variant.Plain ? 'row' : flexDirectionProp;
  const theme = useTheme<Theme>();
  const withAuthorize = useAuthorize(authorizationSource);

  const forcedBackgroundColor = StyleSheet.flatten(style)?.backgroundColor ?? '#999999';

  const functionallyDisabled = disabled || loading;

  const centeredContent = textAlign === 'center' && Boolean(prefix || suffix);

  const textProps = getButtonTextProps(functionallyDisabled, variant);
  const disabledStyle = getButtonDisabledStyle({ forcedBackgroundColor, functionallyDisabled, theme, variant });

  const buttonStyle = (state: PressableStateCallbackType) => [style, state.pressed && { opacity: 0.6 }, disabledStyle];

  const onPressWithAuthentication = (e: GestureResponderEvent) => {
    const action = () => onPress?.(e);
    if (authorize) {
      withAuthorize(action);
    } else {
      action();
    }
  };

  const commonProps = {
    alignItems,
    children,
    disabled: functionallyDisabled,
    flexDirection,
    justifyContent,
    onPress: onPressWithAuthentication,
    variant,
    ...rest,
  };

  if (variant === Variant.Plain) {
    return (
      <Base {...commonProps} {...{ style }} ref={innerRef} pressableType={pressableType}>
        {pureChildren ? <PureWrapper>{children}</PureWrapper> : children}
      </Base>
    );
  }

  return (
    <Base
      {...commonProps}
      pressableType={pressableType}
      overflow="hidden"
      ref={innerRef}
      style={buttonStyle}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    >
      <ButtonPrefix flexDirection={flexDirection} prefix={prefix} />
      <ButtonMainContent
        centeredContent={centeredContent}
        flexDirection={flexDirection}
        prefix={prefix}
        pureChildren={pureChildren}
        suffix={suffix}
        textAlign={textAlign}
        textProps={textProps}
      >
        {children}
      </ButtonMainContent>
      <ButtonSuffix flexDirection={flexDirection} suffix={suffix} variant={variant} />
      <ButtonLoadingOverlay disabledStyle={disabledStyle} loading={loading} theme={theme} variant={variant} />
      <ButtonInfoEnd infoButtonOnPress={infoButtonOnPress} pressableType={pressableType} theme={theme} />
    </Base>
  );
}
